import RefreshIcon from "@mui/icons-material/Refresh"
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router"
import {
  useGetSl1ImportFleetQuery,
  useGetSl1ImportJobsQuery,
} from "src/features/things/sl1ImportApi"
import SaLayout from "./SaLayout"
import Sl1ImportFlowDoc from "./sl1Import/Sl1ImportFlowDoc"
import Sl1ImportJobHistory from "./sl1Import/Sl1ImportJobHistory"
import Sl1ImportOverview from "./sl1Import/Sl1ImportOverview"

/**
 * The SL1-import console (LOCKER-382 child D, §D-4).
 *
 * **"SL1" means the smart-locker feature in `mobilus-webapp`**, not SirSteward API
 * v1 — the two are related only in that the webapp called the v1 API. The page
 * says so in its own subtitle, because this is the surface where a reader who has
 * only ever seen `sl1_locker_id` will arrive.
 *
 * READ-ONLY, like `SaReservationSyncsPage`: a recon and an import are both
 * operator-run from a shell, and there is no mutation endpoint behind any of this.
 * The refresh control exists for exactly that reason — runs arrive from outside
 * the app, so nothing here invalidates the cache on its own.
 *
 * Two tabs, one route, `?tab=` in the URL so a link to the job list is a link to
 * the job list. The report detail is its own route (`/sa/sl1-import/:jobId`)
 * rather than a third tab, because it is about ONE run and a tab that only makes
 * sense with a selection is a tab that is broken half the time.
 */
export const SaSl1ImportPage: FC = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const raw = searchParams.get("tab")
  const tab = raw === "jobs" || raw === "doc" ? raw : "overview"

  const fleetQuery = useGetSl1ImportFleetQuery({ limit: 500 })
  const jobsQuery = useGetSl1ImportJobsQuery({ limit: 500 })
  const fetching = fleetQuery.isFetching || jobsQuery.isFetching

  return (
    <SaLayout selectedMenuItemLabelKey="sl1Import">
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, md: 5 }, py: { xs: 4, md: 5 } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: "1.3rem", fontWeight: 600 }}>
            {t("sl1Import")}
          </Typography>
          <Tooltip title={t("sl1RefreshHint")}>
            <span>
              <IconButton
                size="small"
                aria-label={t("sl1RefreshHint")}
                disabled={fetching}
                onClick={() => {
                  void fleetQuery.refetch()
                  void jobsQuery.refetch()
                }}
              >
                {fetching ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", maxWidth: "82ch", mb: 3 }}
        >
          {t("sl1ImportSubtitle")}
        </Typography>

        <Tabs
          value={tab}
          // Write whatever tab was clicked, and clear the param ONLY for the
          // default. The previous form hard-coded the single non-default tab
          // (`v === "jobs" ? … : {}`), so adding a third tab silently routed it
          // to the else branch: clicking Doc cleared the param and landed back on
          // Overview, which looks exactly like a dead tab.
          onChange={(_e, v) =>
            setSearchParams(v === "overview" ? {} : { tab: String(v) })
          }
          sx={{ mb: 3 }}
        >
          <Tab value="overview" label={t("sl1TabOverview")} />
          <Tab value="jobs" label={t("sl1TabJobs")} />
          {/* Doc LAST: reference material for an operator about to run something,
              not a dashboard. Same placement as the sstv2 migration page's. */}
          <Tab value="doc" label={t("sl1TabDoc")} />
        </Tabs>

        {
          {
            overview: <Sl1ImportOverview />,
            jobs: <Sl1ImportJobHistory />,
            doc: <Sl1ImportFlowDoc />,
          }[tab]
        }
      </Box>
    </SaLayout>
  )
}

export default SaSl1ImportPage
